import clsx from 'clsx';
import { ButtonHTMLAttributes } from 'react';
import { Button as AriaButton, ButtonProps } from 'react-aria-components';

import { focusRingClassName } from '#/shared/utils/domHelper';

import { MaterialIcon } from './MaterialIcon';

type Sizes = 'medium' | 'large';

type Props = ButtonHTMLAttributes<HTMLButtonElement> &
  ButtonProps & {
    variant?: Variant;
    size?: Sizes;
    isLoading?: boolean;
    className?: string;
  };

const sizeClasses: Record<Sizes, string> = {
  medium: 'h-[42px] px-12 py-0',
  large: 'h-[50px] px-16 py-0',
};

export type Variant = 'primary' | 'primaryOutline' | 'error' | 'outline';

type StyleOutput = {
  base: string;
  hover?: string;
  focus?: string;
  active?: string;
  disabled?: string;
};

const getTailwindButtonStyles = (variant: Variant): StyleOutput => {
  switch (variant) {
    case 'primary':
      return {
        base: 'bg-primary text-on-primary rounded-sm border border-primary',
        hover: 'hover:bg-blue-900 text-on-primary hover:cursor-pointer',
        focus: `focus:border-blue-300 ${focusRingClassName}`,
        active: 'active:bg-blue-900 active:border-blue-900 text-on-primary',
        disabled: 'bg-grey-200 text-grey-800 rounded-sm cursor-not-allowed',
      };

    case 'primaryOutline':
      return {
        base: 'bg-transparent text-blue-600 rounded-sm border border-blue-600',
        hover: 'hover:bg-grey-200 hover:rounded-sm hover:text-blue-600 hover:cursor-pointer',
        focus: `focus:bg-grey-200 ${focusRingClassName}`,
        active: 'active:bg-grey-200 active:rounded-sm active:text-blue-600',
        disabled: 'bg-transparent text-grey-800 rounded-sm border border-grey-800 cursor-not-allowed',
      };

    case 'outline':
      return {
        base: 'bg-transparent text-on-surface rounded-sm border border-on-surface',
        hover: 'hover:bg-grey-200 hover:rounded-sm hover:text-on-surface hover:cursor-pointer',
        focus: `focus:bg-grey-200 ${focusRingClassName}`,
        active: 'active:bg-grey-200 active:rounded-sm active:text-on-surface',
        disabled: 'bg-transparent text-grey-800 rounded-sm border border-grey-800 cursor-not-allowed',
      };

    case 'error':
      return {
        base: 'bg-error text-on-error rounded-sm border border-error',
        hover: 'hover:bg-error-on-surface-variant text-on-error hover:cursor-pointer',
        focus: `focus:bg-error-on-surface-variant ${focusRingClassName}`,
        active: 'active:bg-error-on-surface-variant',
        disabled: 'bg-transparent text-grey-800 rounded-sm border border-grey-800 cursor-not-allowed',
      };
    default:
      return {
        base: '',
        hover: '',
        disabled: '',
      };
  }
};

export function Button({
  variant = 'primary',
  size = 'large',
  isLoading,
  children,
  className,
  isDisabled,
  ...rest
}: Props) {
  const styles = getTailwindButtonStyles(variant);

  const finalClass = clsx(
    'inline-flex items-center justify-center rounded-md transition-all gap-8',
    sizeClasses[size],
    !isDisabled && styles.base,
    !isDisabled && !isLoading && styles.hover,
    !isDisabled && !isLoading && styles.focus,
    !isDisabled && !isLoading && styles.active,
    isDisabled && styles.disabled,
    className,
  );

  return (
    <AriaButton {...rest} className={finalClass} isDisabled={isDisabled}>
      {children}
      {isLoading && <MaterialIcon iconName="progress_activity" className="animate-spin" size={'sm'} />}
    </AriaButton>
  );
}
